<script setup lang="ts">
import type { GameVehicleImage, GameVehicleSummary } from "~~/types/gamedata";

const props = defineProps({
  vehicle: {
    type: Object as () => GameVehicleSummary,
    required: true,
  },
  images: {
    type: Array as () => GameVehicleImage[],
    default: () => [],
  },
});

const activeIndex = ref(0);

watch(
  () => props.images,
  () => { activeIndex.value = 0; },
);

const activeImage = computed(() => props.images[activeIndex.value] ?? null);
const previewSource = computed(() =>
  activeImage.value?.thumbnail
  ?? props.vehicle.image?.thumbnail
  ?? activeImage.value?.url
  ?? props.vehicle.image?.url
  ?? null,
);
const fullSource = computed(() => activeImage.value?.url ?? props.vehicle.image?.url ?? null);
const renderedSource = ref<string | null>(previewSource.value);
let preloader: HTMLImageElement | null = null;
let preloadTimeout: ReturnType<typeof setTimeout> | null = null;

function stopPreload() {
  if (preloadTimeout) clearTimeout(preloadTimeout);
  preloadTimeout = null;
  const image = preloader;
  preloader = null;
  if (image) {
    image.onload = null;
    image.onerror = null;
    image.src = '';
  }
}

watch(
  [previewSource, fullSource],
  ([preview, full]) => {
    renderedSource.value = preview;
    stopPreload();

    if (!import.meta.client || !full || full === preview) return;

    const image = new Image();
    preloader = image;
    image.decoding = 'async';
    image.onload = () => {
      if (preloader !== image) return;
      renderedSource.value = full;
      stopPreload();
    };
    image.onerror = stopPreload;
    image.src = full;

    // Langsame Media-Responses niemals minutenlang offen halten. Das bereits
    // sichtbare Thumbnail bleibt in diesem Fall als belastbarer Fallback.
    preloadTimeout = setTimeout(stopPreload, 30_000);
  },
  { immediate: true },
);

onBeforeUnmount(stopPreload);
</script>

<template>
  <div class="rounded-lg overflow-clip border border-primary/20 relative">
    <img
      v-if="renderedSource"
      :src="renderedSource"
      :alt="vehicle?.name ?? 'Schiffsbild'"
      class="h-full w-full object-cover"
      decoding="async"
      fetchpriority="high"
      loading="eager"
    >
    <div
      v-else
      class="flex h-full min-h-72 w-full items-center justify-center bg-bunker-900/60 text-neutral-500"
    >
      <UIcon name="i-lucide-rocket" class="size-8" />
    </div>
  </div>
</template>
